import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, XCircle, AlertTriangle, Target, Zap, ShieldCheck } from 'lucide-react';
import { CtaButton } from './CtaButton';
const FadeIn = ({
  children,
  delay = 0
}: {
  children: React.ReactNode;
  delay?: number;
}) => <motion.div initial={{
  opacity: 0,
  y: 20
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-80px'
}} transition={{
  duration: 0.5,
  delay
}} data-id="element-74">
    {children}
  </motion.div>;
export function SalesContent() {
  return <div className="max-w-3xl mx-auto px-4 py-8 space-y-12 md:space-y-16 text-base md:text-lg text-gray-300 leading-relaxed" data-id="element-75">
      {/* Truth Section */}
      <section className="bg-gray-900/50 border border-gray-800 rounded-2xl p-6 md:p-8 relative overflow-hidden" data-id="element-76">
        <div className="absolute top-0 left-0 w-2 h-full bg-red-500" data-id="element-77"></div>
        <FadeIn data-id="element-78">
          <h2 className="text-xl md:text-2xl font-bold text-white mb-4 flex items-center gap-3" data-id="element-79">
            <AlertTriangle className="text-red-500 w-7 h-7 flex-shrink-0" data-id="element-80" />
            La verdad incómoda que nadie te dice
          </h2>
          <p className="mb-4" data-id="element-81">
            No importa cuántas ganas tengas. No importa cuánto te esfuerces.
          </p>
          <p className="font-bold text-white text-lg mb-4" data-id="element-82">
            Si no tenés un sistema… estás improvisando.
          </p>
          <p className="mb-3" data-id="element-83">Y eso se nota. Se nota en:</p>
          <ul className="space-y-2 mb-6" data-id="element-84">
            {['Jugadores desordenados', 'Ejercicios sin conexión', 'Entrenamientos que no generan progreso', 'Partidos que se pierden por errores evitables'].map((item, i) => <li key={i} className="flex items-center gap-2 text-sm md:text-base" data-id="element-85">
                <XCircle className="text-red-500 w-5 h-5 flex-shrink-0" data-id="element-86" />
                <span data-id="element-87">{item}</span>
              </li>)}
          </ul>
          <p className="mb-2" data-id="element-88">Y lo peor…</p>
          <p className="font-bold text-white text-sm md:text-base" data-id="element-89">
            Se nota en vos. En cómo dudás. En cómo explicás. En cómo dirigís.
          </p>
        </FadeIn>
      </section>

      {/* Vision Section - Made more visible */}
      <section className="bg-gradient-to-br from-gold/10 to-gold/5 border-2 border-gold/40 rounded-2xl p-6 md:p-10 relative overflow-hidden" data-id="element-90">
        <div className="absolute top-0 right-0 w-40 h-40 bg-gold/10 rounded-full blur-3xl pointer-events-none" data-id="element-91"></div>
        <FadeIn data-id="element-92">
          <h2 className="text-3xl md:text-4xl font-black text-gold mb-6" data-id="element-93">
            Imaginate esto…
          </h2>
          <p className="text-lg md:text-xl text-white mb-6" data-id="element-94">
            Llegás al entrenamiento y ya sabés exactamente qué hacer.
          </p>
          <div className="space-y-3 mb-8 pl-5 border-l-4 border-gold" data-id="element-95">
            <p className="text-lg md:text-xl font-semibold text-white" data-id="element-96">
              Cada ejercicio tiene un propósito.
            </p>
            <p className="text-lg md:text-xl font-semibold text-white" data-id="element-97">
              Cada sesión tiene una lógica.
            </p>
            <p className="text-lg md:text-xl font-semibold text-white" data-id="element-98">
              Cada semana tiene una progresión.
            </p>
          </div>
          <p className="mb-4 font-bold text-white text-lg" data-id="element-99">Tus jugadores:</p>
          <ul className="space-y-3 mb-8" data-id="element-100">
            {['Entienden más rápido', 'Ejecutan mejor', 'Mejoran de verdad'].map((item, i) => <li key={i} className="flex items-center gap-3 text-base md:text-lg" data-id="element-101">
                <Target className="text-gold w-6 h-6 flex-shrink-0" data-id="element-102" />
                <span className="font-medium text-white" data-id="element-103">{item}</span>
              </li>)}
          </ul>
          <p className="mb-3 text-lg" data-id="element-104">Y vos…</p>
          <p className="text-2xl md:text-3xl font-black text-gold" data-id="element-105">
            Te sentís seguro. Firme. Claro. Respetado.
          </p>
        </FadeIn>
      </section>

      {/* Solution Title */}
      <section className="text-center" data-id="element-106">
        <FadeIn data-id="element-107">
          <h2 className="text-3xl md:text-4xl font-black text-gold" data-id="element-108">
            La solución: Entrenador de Futsal 360°
          </h2>
        </FadeIn>
      </section>

      {/* What's included */}
      <section data-id="element-109">
        <FadeIn data-id="element-110">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-6 text-center" data-id="element-111">
            ¿Qué incluye?
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-112">
            {['Sistema completo de entrenamientos', '4 tomos de 60 páginas', 'Progresiones tácticas claras', 'Organización semanal y mensual', 'Estructura lista para aplicar'].map((item, i) => <div key={i} className="bg-gradient-to-br from-gray-800 to-gray-900 p-4 rounded-xl border border-gray-700 flex items-center gap-3" data-id="element-113">
                <div className="bg-gold/20 p-2 rounded-lg" data-id="element-114">
                  <ShieldCheck className="text-gold w-5 h-5" data-id="element-115" />
                </div>
                <span className="font-bold text-white text-sm md:text-base" data-id="element-116">
                  {item}
                </span>
              </div>)}
          </div>
        </FadeIn>
      </section>

      {/* 30-day promise */}
      <section data-id="element-117">
        <FadeIn data-id="element-118">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-6 text-center" data-id="element-119">
            En menos de 30 días podés lograr esto
          </h2>
          <div className="grid grid-cols-2 gap-3" data-id="element-120">
            {['Dejar de improvisar completamente', 'Tener entrenamientos estructurados', 'Ver mejoras reales en tu equipo', 'Sentirte seguro en cada sesión'].map((item, i) => <div key={i} className="bg-gray-900 p-4 rounded-xl border border-gray-800 flex flex-col items-center text-center gap-2" data-id="element-121">
                <Zap className="text-gold w-6 h-6" data-id="element-122" />
                <span className="font-bold text-white text-sm" data-id="element-123">{item}</span>
              </div>)}
          </div>
        </FadeIn>
      </section>

      {/* Who is it for & Inaction */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-124">
        <FadeIn delay={0.1} data-id="element-125">
          <div className="bg-gray-800/50 p-6 rounded-2xl border border-gray-700 h-full" data-id="element-126">
            <h3 className="text-xl font-bold text-white mb-4" data-id="element-127">
              ¿Para quién es esto?
            </h3>
            <p className="mb-3 text-sm" data-id="element-128">Para entrenadores que:</p>
            <ul className="space-y-2" data-id="element-129">
              {['Están cansados de improvisar', 'Quieren resultados reales', 'Quieren destacarse', 'Quieren ser tomados en serio'].map((item, i) => <li key={i} className="flex items-center gap-2 text-sm" data-id="element-130">
                  <CheckCircle2 className="text-gold w-4 h-4" data-id="element-131" />
                  <span data-id="element-132">{item}</span>
                </li>)}
            </ul>
          </div>
        </FadeIn>

        <FadeIn delay={0.2} data-id="element-133">
          <div className="bg-red-900/10 p-6 rounded-2xl border border-red-900/30 h-full" data-id="element-134">
            <h3 className="text-xl font-bold text-white mb-4" data-id="element-135">
              ¿Y si no hacés nada?
            </h3>
            <p className="mb-2 text-sm" data-id="element-136">Todo sigue igual.</p>
            <p className="mb-2 text-gray-400 text-sm" data-id="element-137">
              Más dudas. Más frustración. Más errores.
            </p>
            <p className="mb-2 text-sm" data-id="element-138">Y probablemente…</p>
            <p className="font-bold text-red-400 text-sm" data-id="element-139">
              Más oportunidades perdidas.
            </p>
          </div>
        </FadeIn>
      </section>

      {/* Decision */}
      <section className="text-center space-y-4 pb-8 border-b border-gray-800" data-id="element-140">
        <FadeIn data-id="element-141">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4" data-id="element-142">
            La decisión es simple
          </h2>
          <p className="text-lg mb-3" data-id="element-143">Podés seguir como estás…</p>
          <p className="text-xl font-bold text-gold" data-id="element-144">
            O podés convertirte en un entrenador con método, claridad y
            resultados.
          </p>
        </FadeIn>
      </section>
    </div>;
}